import type { BruteForcePlayer, MetricTierSkew } from '../types';

/**
 * Maximum acceptable gap for any single metric before penalty kicks in.
 * If any metric (attack, defense, gameIq, gk) exceeds this threshold,
 * an exponential penalty is applied to discourage heavily imbalanced teams.
 */
export const MAX_METRIC_GAP_THRESHOLD = 0.25;

/**
 * Calculate the average of a numeric array
 */
function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * Calculate penalty for exceeding max metric gap threshold.
 * Uses exponential penalty to strongly discourage imbalanced single metrics.
 *
 * @param maxGap - The largest gap among all metrics
 * @param threshold - The acceptable threshold (default: 0.25)
 * @returns Penalty score (0 if under threshold, exponentially increasing above)
 */
function calculateMaxGapPenalty(maxGap: number, threshold: number = MAX_METRIC_GAP_THRESHOLD): number {
  if (maxGap <= threshold) return 0;

  // Exponential penalty: squared to strongly discourage exceeding threshold
  // Multiplier of 4 scales the overage before squaring for meaningful impact
  const overage = maxGap - threshold;
  return Math.pow(overage * 4, 2);
}

/**
 * Pick the GK rating that best represents a team's net-strength.
 * If the team has a permanent GK, use the highest-rated permanent GK's rating
 * directly. Otherwise, fall back to the team-wide average (rotating keepers).
 */
function teamGkRating(team: BruteForcePlayer[]): number {
  const permanentGKs = team.filter((p) => p.isPermanentGK);
  if (permanentGKs.length > 0) {
    return Math.max(...permanentGKs.map((p) => p.gk));
  }
  return average(team.map((p) => p.gk));
}

/**
 * Uneven teams (9 v 8, 8 v 7…): the short team's ratings are scaled down before
 * comparing, so balance asks it to be stronger per player to make up for the
 * missing man. Halfway between "equal averages" (factor 1) and "equal totals"
 * (factor size/bigger size): 9 v 8 → ×0.944, i.e. the 8 average ~6% higher.
 * Equal team sizes → 1 for both, so even games score exactly as before.
 */
export function manpowerFactor(teamSize: number, otherTeamSize: number): number {
  const larger = Math.max(teamSize, otherTeamSize);
  if (teamSize >= larger || larger === 0) return 1;
  return (1 + teamSize / larger) / 2;
}

/** Attack/Defense/Game IQ/GK for a team, with the short-team scaling applied */
function effectiveRatings(team: BruteForcePlayer[], otherTeam: BruteForcePlayer[]) {
  const outfield = team.filter((p) => !p.isPermanentGK);
  const factor = manpowerFactor(team.length, otherTeam.length);
  return {
    attack: average(outfield.map((p) => p.attack)) * factor,
    defense: average(outfield.map((p) => p.defense)) * factor,
    gameIq: average(team.map((p) => p.gameIq)) * factor,
    // One keeper either way — not scaled
    gk: teamGkRating(team),
  };
}

/**
 * Calculate the core ratings balance score
 * Measures the difference in Attack, Defense, Game IQ, and GK between teams.
 *
 * Permanent goalkeepers are EXCLUDED from Attack and Defense averages
 * (they're not playing outfield). They are INCLUDED in Game IQ (positioning
 * still matters). For the GK metric, a team with a permanent GK uses that
 * player's rating directly; teams without one use the team-average (rotating
 * keeper model). With uneven team sizes the short team is scaled by
 * manpowerFactor().
 *
 * Returns: A normalized score where 0 = perfect balance, higher = worse
 */
export function calculateCoreRatingsScore(
  blueTeam: BruteForcePlayer[],
  orangeTeam: BruteForcePlayer[]
): number {
  const blue = effectiveRatings(blueTeam, orangeTeam);
  const orange = effectiveRatings(orangeTeam, blueTeam);

  // Calculate absolute differences
  const attackDiff = Math.abs(blue.attack - orange.attack);
  const defenseDiff = Math.abs(blue.defense - orange.defense);
  const gameIqDiff = Math.abs(blue.gameIq - orange.gameIq);
  const gkDiff = Math.abs(blue.gk - orange.gk);

  // Find the maximum gap among all metrics
  const maxGap = Math.max(attackDiff, defenseDiff, gameIqDiff, gkDiff);

  // Calculate penalty if any single metric exceeds threshold
  const maxGapPenalty = calculateMaxGapPenalty(maxGap);

  // Average of all differences
  // Ratings are on 1-10 scale, so max diff is 9
  // Normalize to roughly 0-1 scale by dividing by 9
  const totalDiff = (attackDiff + defenseDiff + gameIqDiff + gkDiff) / 4;
  const baseScore = totalDiff / 9;

  // Return base score + penalty (penalty makes high-gap combinations uncompetitive)
  return baseScore + maxGapPenalty;
}

/**
 * Maximum tier skew allowed per metric before penalty kicks in.
 * Allows 1 player difference per metric between teams.
 */
export const MAX_TIER_SKEW = 1;

/**
 * Calculate penalty for per-metric tier imbalance.
 *
 * This prevents scenarios where one team gets all the top attackers
 * or all the top defenders, even if the overall ratings are balanced.
 *
 * @param blueTeam - Players on blue team
 * @param orangeTeam - Players on orange team
 * @param allPlayers - All players (for determining "top third" per metric)
 * @returns Total penalty and details per metric
 */
export function calculatePerMetricTierPenalty(
  blueTeam: BruteForcePlayer[],
  orangeTeam: BruteForcePlayer[],
  allPlayers: BruteForcePlayer[]
): { totalPenalty: number; details: MetricTierSkew[] } {
  const metrics: Array<'attack' | 'defense' | 'gameIq' | 'gk'> = [
    'attack',
    'defense',
    'gameIq',
    'gk',
  ];
  const details: MetricTierSkew[] = [];
  let totalPenalty = 0;

  // For attack/defense, derive "top third" from outfield-only players
  // (permanent GKs don't play outfield, so their attack/defense rank is meaningless)
  const outfieldPlayers = allPlayers.filter((p) => !p.isPermanentGK);

  for (const metric of metrics) {
    const isOutfieldMetric = metric === 'attack' || metric === 'defense';
    const pool = isOutfieldMetric ? outfieldPlayers : allPlayers;

    // Sort by this specific metric to find "top third"
    const sorted = [...pool].sort((a, b) => b[metric] - a[metric]);
    const topThirdSize = Math.ceil(sorted.length / 3);
    const topIds = new Set(sorted.slice(0, topThirdSize).map((p) => p.player_id));

    // For outfield metrics, only count outfield players on each team
    const blueCandidates = isOutfieldMetric ? blueTeam.filter((p) => !p.isPermanentGK) : blueTeam;
    const orangeCandidates = isOutfieldMetric ? orangeTeam.filter((p) => !p.isPermanentGK) : orangeTeam;

    const blueTopCount = blueCandidates.filter((p) => topIds.has(p.player_id)).length;
    const orangeTopCount = orangeCandidates.filter((p) => topIds.has(p.player_id)).length;
    const skew = Math.abs(blueTopCount - orangeTopCount);

    // Apply soft penalty for skew > MAX_TIER_SKEW
    // Squared to strongly discourage large skews
    const penalty =
      skew > MAX_TIER_SKEW ? Math.pow((skew - MAX_TIER_SKEW) * 0.1, 2) : 0;

    details.push({ metric, blueTopCount, orangeTopCount, skew, penalty });
    totalPenalty += penalty;
  }

  // Average the penalty across all metrics
  return { totalPenalty: totalPenalty / metrics.length, details };
}

/**
 * Core ratings breakdown with penalty information
 */
export interface CoreRatingsBreakdown {
  /** Raw team averages (for display) */
  blue: { attack: number; defense: number; gameIq: number; gk: number };
  orange: { attack: number; defense: number; gameIq: number; gk: number };
  /** Gaps the scorer uses — after the short-team scaling when sizes differ */
  gaps: { attack: number; defense: number; gameIq: number; gk: number };
  /** Short-team scaling applied to each side (1 = none) */
  manpower: { blue: number; orange: number };
  maxGap: {
    metric: 'attack' | 'defense' | 'gameIq' | 'gk';
    value: number;
    exceedsThreshold: boolean;
    threshold: number;
  };
  penalty: {
    applied: boolean;
    amount: number;
  };
  // Per-metric tier penalty (if enabled)
  perMetricTierPenalty?: {
    applied: boolean;
    amount: number;
    details: MetricTierSkew[];
  };
}

/**
 * Get detailed breakdown of core ratings balance
 */
export function getCoreRatingsBreakdown(
  blueTeam: BruteForcePlayer[],
  orangeTeam: BruteForcePlayer[]
): CoreRatingsBreakdown {
  const blueOutfield = blueTeam.filter((p) => !p.isPermanentGK);
  const orangeOutfield = orangeTeam.filter((p) => !p.isPermanentGK);

  const blue = {
    attack: average(blueOutfield.map((p) => p.attack)),
    defense: average(blueOutfield.map((p) => p.defense)),
    gameIq: average(blueTeam.map((p) => p.gameIq)),
    gk: teamGkRating(blueTeam),
  };

  const orange = {
    attack: average(orangeOutfield.map((p) => p.attack)),
    defense: average(orangeOutfield.map((p) => p.defense)),
    gameIq: average(orangeTeam.map((p) => p.gameIq)),
    gk: teamGkRating(orangeTeam),
  };

  const blueEffective = effectiveRatings(blueTeam, orangeTeam);
  const orangeEffective = effectiveRatings(orangeTeam, blueTeam);
  const gaps = {
    attack: Math.abs(blueEffective.attack - orangeEffective.attack),
    defense: Math.abs(blueEffective.defense - orangeEffective.defense),
    gameIq: Math.abs(blueEffective.gameIq - orangeEffective.gameIq),
    gk: Math.abs(blueEffective.gk - orangeEffective.gk),
  };

  // Determine which metric has the largest gap
  const gapEntries: Array<{ metric: 'attack' | 'defense' | 'gameIq' | 'gk'; value: number }> = [
    { metric: 'attack', value: gaps.attack },
    { metric: 'defense', value: gaps.defense },
    { metric: 'gameIq', value: gaps.gameIq },
    { metric: 'gk', value: gaps.gk },
  ];
  const maxGapEntry = gapEntries.reduce((max, entry) => (entry.value > max.value ? entry : max));

  const exceedsThreshold = maxGapEntry.value > MAX_METRIC_GAP_THRESHOLD;
  const penaltyAmount = calculateMaxGapPenalty(maxGapEntry.value);

  return {
    blue,
    orange,
    gaps,
    manpower: {
      blue: manpowerFactor(blueTeam.length, orangeTeam.length),
      orange: manpowerFactor(orangeTeam.length, blueTeam.length),
    },
    maxGap: {
      metric: maxGapEntry.metric,
      value: maxGapEntry.value,
      exceedsThreshold,
      threshold: MAX_METRIC_GAP_THRESHOLD,
    },
    penalty: {
      applied: exceedsThreshold,
      amount: penaltyAmount,
    },
  };
}
